import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { MessageConversation, OutboxMessage } from '../api/types.js';
import { useConversations, useDecideMessage, useOutboxMessages, usePeers, useSetPeers, useStopConversation } from '../hooks/use-messages.js';
import { useServers } from '../hooks/use-servers.js';
import { useSessions } from '../hooks/use-sessions.js';
import { Button } from './button.js';
import { SectionAction, SettingsSection } from './settings-section.js';

// Messages between servers (ADR-025 step 2). A session of this server wants to write to a session of another server,
// another client: the operator reads exactly what would leave, picks who receives it, and sends or rejects it. The
// direction carries the decision, so it heads each card. Plain text only.
//
// Step 3: the operator may let the two sessions go on without him for an hour, and a message to a stopped server waits
// for it, started or not.

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** A conversation allowed until a time, with Stop: the next message between the two sessions waits for the operator again */
function Conversation({ serverId, c, isAdmin, label }: { serverId: string; c: MessageConversation; isAdmin: boolean; label: React.ReactNode }) {
  const stop = useStopConversation(serverId);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" style={{ color: 'var(--color-text-2)' }}>
      <span>{label} goes on without you until {hhmm(c.until)}.</span>
      {isAdmin && (
        <Button size="sm" variant="ghost" className="max-sm:min-h-10" disabled={stop.isPending} onClick={() => stop.mutate(c)}>
          {stop.isPending ? 'Stopping…' : 'Stop'}
        </Button>
      )}
      {stop.isError && <span role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not stopped: {(stop.error as Error).message}</span>}
    </div>
  );
}

function ago(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (m < 60) return `${Math.max(m, 1)} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function Text({ text }: { text: string }) {
  const [all, setAll] = useState(false);
  const long = text.length > 600 || text.split('\n').length > 8;
  return (
    <div className="mt-2 rounded-md px-3 py-2" style={{ backgroundColor: 'var(--color-surface-2)', border: '1px solid var(--color-border)' }}>
      <p className={`max-w-[72ch] whitespace-pre-wrap break-words text-sm ${!all && long ? 'line-clamp-8' : ''}`} style={{ color: 'var(--color-text)' }}>{text}</p>
      {long && (
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all} className="mt-1 py-0.5 text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
          {all ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}

/** The receiving session on the target server: one of its sessions, or a new one */
function ReceiverPicker({ target, value, onChange, suggested }: { target: string; value: string; onChange: (v: string) => void; suggested?: string }) {
  const { data: sessions } = useSessions(target);
  const live = (sessions ?? []).filter((s) => s.status !== 'terminated' || s.id === suggested);
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs sm:flex-row sm:items-center sm:gap-2" style={{ color: 'var(--color-text-2)' }}>
      Receiver on {target}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={`Receiving session on ${target}`}
        className="min-w-0 max-w-full rounded-md px-2 py-1.5 text-sm focus-visible:outline focus-visible:outline-2 sm:max-w-xs"
        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' }}>
        <option value="">A new session</option>
        {live.map((s) => (
          <option key={s.id} value={s.id}>{(s.name || s.id.slice(0, 8)) + (s.id === suggested ? ' (the one that wrote to you)' : '')}</option>
        ))}
      </select>
    </label>
  );
}

/** One message; `inSession` in the session that wrote it, which needs no link to itself (ADR-039 8c step 10) */
export function MessageCard({ serverId, m, isAdmin, focused, onDecided, inSession = false }: { serverId: string; m: OutboxMessage; isAdmin: boolean; focused: boolean; onDecided: (id: string) => void; inSession?: boolean }) {
  const decide = useDecideMessage(serverId);
  const { data: servers } = useServers();
  const { data: conversations } = useConversations(serverId);
  const [receiver, setReceiver] = useState(m.suggestedSession ?? '');
  const [allow, setAllow] = useState(false);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);
  const current = decide.data?.message ?? m;
  const error = decide.error as Error | null;
  const done = () => onDecided(m.id);
  // Unknown until the servers load: the plain Send, which says so if the target is not running
  const targetStatus = servers?.find((s) => s.id === m.to)?.status;
  const targetDown = !!targetStatus && targetStatus !== 'running';
  const sending = (start: boolean) => decide.isPending && decide.variables?.decision === 'send' && !!decide.variables.start === start;
  const sendIt = (start = false) => decide.mutate({ id: m.id, decision: 'send', sessionId: receiver || null, allowConversation: allow, start }, { onSuccess: done });
  const conversation = current.status === 'delivered' && current.deliveredTo
    ? conversations?.find((c) => c.session === m.requestedBySession && c.peer === m.to && c.peerSession === current.deliveredTo)
    : undefined;

  return (
    <article ref={ref} id={`message-${m.id}`} aria-labelledby={`message-${m.id}-title`} className="card px-4 py-3"
      style={{ borderLeft: '3px solid var(--color-warn)', ...(focused ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 id={`message-${m.id}-title`} className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          {serverId} <span aria-hidden="true" style={{ color: 'var(--color-text-3)' }}>›</span><span className="sr-only">to</span> {m.to}
          {m.replyTo && <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-text-3)' }}>an answer</span>}
        </h3>
        <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{ago(m.createdAt)}</span>
      </div>
      <p className="mt-1 text-xs" style={{ color: 'var(--color-text-2)' }}>
        This text leaves {serverId} for {m.to}: check it holds nothing {m.to} should not see.
        {m.reason && <> Why: {m.reason}.</>}
        {m.requestedBySession && !inSession && <> Written by <Link to={`/servers/${serverId}/sessions/${m.requestedBySession}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>a session</Link>.</>}
      </p>
      <Text text={m.text} />

      <div className="mt-3 space-y-2">
        {current.status === 'failed' && <p className="text-sm" style={{ color: 'var(--color-error)' }}>Not delivered: {current.error}</p>}
        {(current.status === 'pending' || current.status === 'failed') && isAdmin && (
          <>
            <ReceiverPicker target={m.to} value={receiver} onChange={setReceiver} suggested={m.suggestedSession} />
            {m.requestedBySession && (
              <label className="flex min-h-10 items-start gap-2 text-sm sm:min-h-8 sm:items-center" style={{ color: 'var(--color-text)' }}>
                <input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-brand)] sm:mt-0" />
                <span>Allow this conversation for 1 hour <span style={{ color: 'var(--color-text-3)' }}>(their next messages, both ways, go without asking you)</span></span>
              </label>
            )}
            {targetDown && <p className="text-xs" style={{ color: 'var(--color-text-2)' }}>{m.to} is {targetStatus}: the message goes once it runs.</p>}
            <div className="flex flex-wrap items-center gap-2">
              {targetDown && targetStatus === 'stopped' && (
                <Button size="sm" className="max-sm:min-h-10" disabled={decide.isPending} onClick={() => sendIt(true)}>
                  {sending(true) ? 'Starting…' : `Start ${m.to} and send`}
                </Button>
              )}
              <Button size="sm" variant={targetDown && targetStatus === 'stopped' ? 'ghost' : undefined} className="max-sm:min-h-10" disabled={decide.isPending} onClick={() => sendIt()}>
                {sending(false) ? 'Sending…' : targetDown ? 'Send when it runs' : `${current.status === 'failed' ? 'Send again' : 'Send'} to ${m.to}`}
              </Button>
              <Button size="sm" variant="ghost" className="max-sm:min-h-10" disabled={decide.isPending}
                onClick={() => decide.mutate({ id: m.id, decision: 'reject' }, { onSuccess: done })}>Reject</Button>
            </div>
          </>
        )}
        {current.status === 'pending' && !isAdmin && <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Waiting for an admin of this server to decide.</p>}
        {current.status === 'approved' && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>
              Approved: it goes to {current.receiver ? 'the session you picked' : 'a new session'} once {m.to} runs{targetStatus ? ` (${targetStatus} now)` : ''}.
            </p>
            {isAdmin && targetStatus === 'stopped' && (
              <Button size="sm" variant="ghost" className="max-sm:min-h-10" disabled={decide.isPending}
                onClick={() => decide.mutate({ id: m.id, decision: 'send', sessionId: current.receiver ?? null, allowConversation: current.allowConversation, start: true })}>
                {sending(true) ? 'Starting…' : `Start ${m.to}`}
              </Button>
            )}
          </div>
        )}
        {current.status === 'delivered' && (
          <p className="text-sm" style={{ color: 'var(--color-success)' }}>
            Sent to{' '}
            <Link to={`/servers/${m.to}/sessions/${current.deliveredTo}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>its session on {m.to}</Link>
            {current.inConversation ? ', without asking: in the conversation you allowed.' : '.'}
          </p>
        )}
        {conversation && <Conversation serverId={serverId} c={conversation} isAdmin={isAdmin} label="This conversation" />}
        {current.status === 'rejected' && <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Rejected: nothing left {serverId}.</p>}
        {current.status === 'sending' && <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Sending…</p>}
        {error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not sent: {error.message}</p>}
      </div>
    </article>
  );
}

/** « Messages to send »: the messages waiting for the operator (Sessions tab); nothing when none waits */
export function MessagesToSend({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data } = useOutboxMessages(serverId);
  const [params] = useSearchParams();
  const focus = params.get('message');
  const [decidedHere, setDecidedHere] = useState<Set<string>>(() => new Set());
  const { data: conversations } = useConversations(serverId);
  const open = (data ?? []).filter((m) => m.status === 'pending' || m.status === 'approved' || m.status === 'sending' || m.status === 'failed' || decidedHere.has(m.id));
  const live = conversations ?? [];
  if (!open.length && !live.length) return null;
  return (
    <section aria-labelledby="messages-title" className="mb-8 min-w-0">
      <h2 id="messages-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Messages to send</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-2)' }}>
        Written by this server&apos;s sessions for another server&apos;s. Nothing leaves until you send it.
      </p>
      {live.length > 0 && (
        <ul className="mt-3 space-y-1" aria-label="Conversations allowed">
          {live.map((c) => (
            <li key={`${c.session}-${c.peer}-${c.peerSession}`}>
              <Conversation serverId={serverId} c={c} isAdmin={isAdmin} label={<>
                The conversation between <Link to={`/servers/${serverId}/sessions/${c.session}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>a session</Link> and{' '}
                <Link to={`/servers/${c.peer}/sessions/${c.peerSession}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>one on {c.peer}</Link>
              </>} />
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 space-y-3">
        {open.map((m) => (
          <MessageCard key={m.id} serverId={serverId} m={m} isAdmin={isAdmin} focused={m.id === focus}
            onDecided={(id) => setDecidedHere((prev) => new Set(prev).add(id))} />
        ))}
      </div>
    </section>
  );
}

const DECIDED: Partial<Record<OutboxMessage['status'], string>> = { delivered: 'sent', rejected: 'rejected', failed: 'not delivered' };

/**
 * Settings: which servers this one may exchange messages with, and the messages decided in the last 30 days. Always
 * shown, with its state: loading, why the pairings could not be read, or the servers to tick.
 */
export function ServerMessaging({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const servers = useServers();
  const peersQuery = usePeers(serverId);
  const { data: messages } = useOutboxMessages(serverId);
  const save = useSetPeers(serverId);
  const [params] = useSearchParams();
  const focus = params.get('message');
  const peers = peersQuery.data;
  const others = (servers.data ?? []).map((s) => s.id).filter((id) => id !== serverId).sort();
  const decided = (messages ?? []).filter((m) => m.status === 'delivered' || m.status === 'rejected');
  const failed = (peersQuery.error ?? (servers.data ? null : servers.error)) as Error | null;
  const loading = !failed && (!peers || !servers.data);

  let state: string | undefined;
  if (failed) state = `Pairings not loaded: ${failed.message}`;
  else if (!loading && !others.length) state = 'No other server to pair with yet.';
  else if (!loading) state = `This server's sessions may write to the sessions of the servers ticked here, and theirs to it. Each message still waits for your approval.`;
  const retry = failed && (
    <SectionAction onClick={() => { void peersQuery.refetch(); if (servers.error) void servers.refetch(); }} disabled={peersQuery.isFetching}>
      {peersQuery.isFetching ? 'Retrying…' : 'Retry'}
    </SectionAction>
  );
  const toggle = (id: string) => peers && save.mutate(peers.includes(id) ? peers.filter((p) => p !== id) : [...peers, id]);

  return (
    <SettingsSection id="messaging-title" title="Messages between servers" state={state} tone={failed ? 'error' : 'normal'} loading={loading} action={retry || undefined}>
      {peers && others.length > 0 && (
        <fieldset className="mt-3" disabled={!isAdmin || save.isPending}>
          <legend className="sr-only">Servers {serverId} may exchange messages with</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {others.map((id) => (
              <label key={id} className="flex min-h-10 items-center gap-2 text-sm sm:min-h-8" style={{ color: 'var(--color-text)' }}>
                <input type="checkbox" checked={peers.includes(id)} onChange={() => toggle(id)} className="h-4 w-4 accent-[var(--color-brand)]" />
                {id}
              </label>
            ))}
          </div>
          {!isAdmin && <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Only an admin of this server changes them.</p>}
          {save.isError && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not saved: {(save.error as Error).message}</p>}
        </fieldset>
      )}
      {decided.length > 0 && <DecidedMessages decided={decided} focus={focus} />}
    </SettingsSection>
  );
}

function DecidedMessages({ decided, focus }: { decided: OutboxMessage[]; focus: string | null }) {
  return (
    <details className="mt-3" open={!!focus && decided.some((m) => m.id === focus)}>
      <summary className="cursor-pointer py-1.5 text-xs max-sm:min-h-10" style={{ color: 'var(--color-text-3)' }}>Decided in the last 30 days ({decided.length})</summary>
      <ul className="mt-2 space-y-1">
        {decided.map((m) => (
          <li key={m.id} id={`message-${m.id}`} className="w-fit max-w-full break-words rounded-sm px-1 text-xs"
            style={{ color: 'var(--color-text-2)', ...(m.id === focus ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
            To {m.to}, {DECIDED[m.status] ?? m.status}: {m.text.length > 90 ? `${m.text.slice(0, 90)}…` : m.text}
          </li>
        ))}
      </ul>
    </details>
  );
}
